import type { LocationStatus } from '@interfaces/IScheduler';
import { GlyphCheck, GlyphHalfDisc, GlyphRing, GlyphSvg } from '@components/ui/StatusGlyphGeometry/StatusGlyphGeometry';

interface IStatusGlyphProps {
  status: LocationStatus;
  size?: number;
}

/**
 * Tracker-local status glyph, tuned to read on the vivid full-cell fills (light
 * mark on a saturated ground). Deliberately NOT the Scheduler's `StatusMark`:
 * that one is used with the opposite colour polarity (saturated mark on a pale
 * ground), so the two surfaces want different weights. They share the SHAPES —
 * `StatusGlyphGeometry` — and each sets its own stroke.
 *
 * Progress metaphor over TRUSSTOR's per-area vocabulary: planned/unplanned =
 * hollow ring, in progress (however Trusstor got there) = half-filled disc,
 * ready for review = a hollow check, complete = a check. A late area
 * (delayed/overdue) gets the cross the old `blocked` used. The legend renders
 * these SAME glyphs so it matches the cells. Uses `currentColor`, inheriting
 * the cell's --tracker-status-*-fg.
 */
export function StatusGlyph({ status, size = 14 }: IStatusGlyphProps) {
  if (status === 'complete') {
    return <GlyphSvg size={size}><GlyphCheck strokeWidth={2.1} /></GlyphSvg>;
  }
  if (status === 'readyForReview') {
    return (
      <GlyphSvg size={size}>
        <circle cx="7" cy="7" r="5.6" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
        <path d="M3.6 7.2 L5.9 9.5 L10.3 4.9" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </GlyphSvg>
    );
  }
  if (status === 'inProgress') {
    return <GlyphSvg size={size}><GlyphHalfDisc strokeWidth={1.6} /></GlyphSvg>;
  }
  if (status === 'delayed' || status === 'delayedOverdue' || status === 'overdue') {
    return (
      <GlyphSvg size={size}>
        <path d="M4.3 4.3 L9.7 9.7 M9.7 4.3 L4.3 9.7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </GlyphSvg>
    );
  }
  // planned / unplanned — a hollow ring (the original mark).
  return <GlyphSvg size={size}><GlyphRing strokeWidth={1.6} /></GlyphSvg>;
}
